// ABOUTME: Renders the favorite star on model-selection product cards, toggling the model's bookmark.
// ABOUTME: A plain component over the catalog's shared favorites: no IsFavorite request and no Apollo hook per card.

import type { IProduct } from '@coolshop/cool-model-selection';
import { Box, IconButton, StarBorderIcon, StarIcon } from '@ds/coolshop';
import { useCanAddFavorite, useFavoritesEnabled } from '@src/_new/modules/user-favorites';
import { SettingsContext } from '@src/modules/setting/SettingsContext';
import { useContext, useEffect } from 'react';
import { useIntl } from 'react-intl';

import { resolveProductFavoriteTarget } from '../../utils/resolveProductFavoriteTarget';
import { useCatalogProductFavorites } from './CatalogProductFavoritesContext';

// `isFavorite` is resolved server-side and carried on the catalog payload; the package type does
// not know about it, like the other host-added product fields.
type ProductWithFavorite = IProduct & {
    isFavorite?: boolean;
    isConfigurable?: boolean;
};

/**
 * Start-action slot for the model-selection product card. Lets the user bookmark a model straight
 * from the catalog, before entering the configurator. Renders nothing when favorites are disabled,
 * when the card does not resolve to a hierarchy node, or when the user's roles may not add a
 * favorite on its hierarchy (`favoriteRoles`) and it is not one already. The state and the toggle are the catalog's
 * (`CatalogProductFavoritesProvider`), so a family of hundreds of cards shares one mutation and one
 * cache watch. The provider is required: outside it the star renders nothing, by design, rather
 * than falling back to a per-card Apollo hook.
 */
export function ProductFavoriteAction(product: IProduct) {
    const { formatMessage } = useIntl();
    const settings = useContext(SettingsContext);
    const favoritesEnabled = useFavoritesEnabled();
    const canAddFavorite = useCanAddFavorite();
    const productFavorites = useCatalogProductFavorites();

    const { id, isFavorite: markedFavorite, isConfigurable } = product as ProductWithFavorite;
    const target = resolveProductFavoriteTarget(
        { id, isConfigurable },
        {
            model: settings.prototype?.hierarchies?.model,
            prototype: settings.prototype?.hierarchies?.prototype,
        },
    );

    const active = favoritesEnabled && !!target && !!productFavorites;
    const register = productFavorites?.register;
    const hierarchyId = target?.hierarchyId;
    const nodeId = target?.nodeId;
    // Follows the cache writes of the session for this card's node while it is mounted.
    useEffect(
        () =>
            active && register && hierarchyId && nodeId
                ? register({ hierarchyId, nodeId })
                : undefined,
        [active, register, hierarchyId, nodeId],
    );

    if (!active || !target || !productFavorites) {
        return null;
    }

    const isFavorite = productFavorites.isFavorite(target, markedFavorite);
    // Removing is always allowed: a favorite already set keeps its star.
    if (!isFavorite && !canAddFavorite(target.hierarchyId)) {
        return null;
    }

    // The card itself is clickable (it opens the configurator), so the star's clicks must not
    // bubble up to it.
    const stopAndRun = (run: () => void) => (event: React.MouseEvent) => {
        event.stopPropagation();
        event.preventDefault();
        run();
    };

    return (
        <Box onClick={stopAndRun(() => undefined)}>
            <IconButton
                size="small"
                aria-label={
                    isFavorite
                        ? formatMessage({
                              id: 'catalog.favorite.remove',
                              defaultMessage: 'Remove from favorites',
                          })
                        : formatMessage({
                              id: 'catalog.favorite.add',
                              defaultMessage: 'Add to favorites',
                          })
                }
                data-testid={`product-favorite-button-${id}`}
                disabled={productFavorites.isToggling(target)}
                onClick={stopAndRun(() => productFavorites.toggle(target, id))}
            >
                {isFavorite ? <StarIcon color="warning" /> : <StarBorderIcon />}
            </IconButton>
        </Box>
    );
}
